/**
 * useOrbitalConfig
 *
 * Orbital Loading UI를 위한 반응형 설정 훅
 * - viewBox, orbits, center, markerSize 등 브레이크포인트별 설정 제공
 * - Golden Angle 기반 좌표 계산 유틸리티 포함
 */

import { useMemo } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * 반응형 Orbital 설정
 * - xs: 360px~ (모바일)
 * - sm: 600px~ (큰 모바일/작은 태블릿)
 * - md: 900px~ (태블릿)
 * - lg: 1200px~ (데스크톱)
 */
const RESPONSIVE_CONFIG = {
  xs: {
    viewBox: { width: 320, height: 400 },
    center: { x: 160, y: 180 },
    orbits: [
      { radius: 70, items: 3, fontSize: 11, rotationDuration: 50 },
      { radius: 130, items: 5, fontSize: 10, rotationDuration: 65 },
    ],
    markerSize: 6,
    centerSize: { width: 140, height: 60 },
  },
  sm: {
    viewBox: { width: 500, height: 500 },
    center: { x: 250, y: 230 },
    orbits: [
      { radius: 90, items: 3, fontSize: 12, rotationDuration: 45 },
      { radius: 160, items: 5, fontSize: 11, rotationDuration: 60 },
      { radius: 230, items: 4, fontSize: 10, rotationDuration: 75 },
    ],
    markerSize: 7,
    centerSize: { width: 180, height: 70 },
  },
  md: {
    viewBox: { width: 700, height: 600 },
    center: { x: 350, y: 280 },
    orbits: [
      { radius: 100, items: 3, fontSize: 13, rotationDuration: 40 },
      { radius: 180, items: 4, fontSize: 12, rotationDuration: 55 },
      { radius: 260, items: 5, fontSize: 11, rotationDuration: 70 },
    ],
    markerSize: 8,
    centerSize: { width: 200, height: 80 },
  },
  lg: {
    viewBox: { width: 900, height: 700 },
    center: { x: 450, y: 320 },
    orbits: [
      { radius: 120, items: 3, fontSize: 14, rotationDuration: 35 },
      { radius: 200, items: 4, fontSize: 13, rotationDuration: 50 },
      { radius: 280, items: 4, fontSize: 12, rotationDuration: 65 },
      { radius: 360, items: 5, fontSize: 11, rotationDuration: 80 },
    ],
    markerSize: 10,
    centerSize: { width: 240, height: 90 },
  },
};

/**
 * Golden Angle (황금각) - 겹침 최소화를 위한 분포 각도
 */
const GOLDEN_ANGLE = 137.508;

/**
 * 궤도 위 아이템 위치 계산 (Golden Angle 분포)
 *
 * @param {number} itemCount - 아이템 개수
 * @param {number} radius - 궤도 반지름
 * @param {number} startAngle - 시작 각도 (도)
 * @param {object} center - 중심점 { x, y }
 * @returns {Array} 위치 배열 [{ x, y, angle }]
 */
export const calculateOrbitalPositions = (itemCount, radius, startAngle = 0, center = { x: 0, y: 0 }) => {
  return Array.from({ length: itemCount }, (_, i) => {
    const angleDeg = startAngle + (i * GOLDEN_ANGLE);
    const angleRad = angleDeg * (Math.PI / 180);
    return {
      x: center.x + Math.cos(angleRad) * radius,
      y: center.y + Math.sin(angleRad) * radius,
      angle: angleDeg % 360,
    };
  });
};

/**
 * 각도 기반 텍스트 앵커 결정 (가독성 최적화)
 *
 * @param {number} angle - 각도 (도)
 * @returns {'start' | 'end' | 'middle'} 텍스트 앵커
 */
export const getTextAnchor = (angle) => {
  const normalizedAngle = ((angle % 360) + 360) % 360;
  if (normalizedAngle > 90 && normalizedAngle < 270) {
    return 'end';
  }
  return 'start';
};

/**
 * 각도 기반 텍스트 오프셋 계산
 *
 * @param {number} angle - 각도 (도)
 * @param {number} baseOffset - 기본 오프셋
 * @returns {object} { dx, dy } 오프셋
 */
export const getTextOffset = (angle, baseOffset = 12) => {
  const normalizedAngle = ((angle % 360) + 360) % 360;
  const rad = normalizedAngle * (Math.PI / 180);

  return {
    dx: Math.cos(rad) * baseOffset,
    dy: Math.sin(rad) * (baseOffset * 0.7),
  };
};

/**
 * 텍스트 truncate 유틸리티
 *
 * @param {string} text - 원본 텍스트
 * @param {number} maxLength - 최대 길이
 * @returns {string} 잘린 텍스트
 */
export const truncateText = (text, maxLength = 30) => {
  if (!text || text.length <= maxLength) return text || '';
  return text.slice(0, maxLength - 1) + '…';
};

/**
 * 궤도 회전 방향 결정 (충돌 방지를 위해 교차)
 *
 * @param {number} orbitIndex - 궤도 인덱스
 * @returns {'cw' | 'ccw'} 시계/반시계 방향
 */
export const getRotationDirection = (orbitIndex) => {
  return orbitIndex % 2 === 0 ? 'cw' : 'ccw';
};

/**
 * 궤도 시작 각도 오프셋 (겹침 방지)
 *
 * @param {number} orbitIndex - 궤도 인덱스
 * @returns {number} 시작 각도 (도)
 */
export const getStartAngle = (orbitIndex) => {
  return orbitIndex * 45;
};

/**
 * useOrbitalConfig 훅
 *
 * @returns {object} 반응형 Orbital 설정
 */
export const useOrbitalConfig = () => {
  const theme = useTheme();

  // 브레이크포인트 체크
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  // lg 이상은 default

  const config = useMemo(() => {
    if (isXs) return RESPONSIVE_CONFIG.xs;
    if (isSm) return RESPONSIVE_CONFIG.sm;
    if (isMd) return RESPONSIVE_CONFIG.md;
    return RESPONSIVE_CONFIG.lg;
  }, [isXs, isSm, isMd]);

  const breakpoint = useMemo(() => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    return 'lg';
  }, [isXs, isSm, isMd]);

  // 전체 아이템 수 계산
  const totalItems = useMemo(() => {
    return config.orbits.reduce((sum, orbit) => sum + orbit.items, 0);
  }, [config.orbits]);

  return {
    ...config,
    breakpoint,
    totalItems,
    calculatePositions: (orbitIndex) => {
      const orbit = config.orbits[orbitIndex];
      if (!orbit) return [];
      return calculateOrbitalPositions(
        orbit.items,
        orbit.radius,
        getStartAngle(orbitIndex),
        config.center
      );
    },
  };
};

export default useOrbitalConfig;
